/**
 * Guide player — spotlight walkthroughs for the guides defined in
 * @proclaw/shared (ui-guides.ts). Serves two entry points:
 *
 *  1. First login: a welcome modal offering the "platform-basics" guide,
 *     remembered per user in localStorage.
 *  2. On demand: `proclaw:start-guide` (detail.guideId) — dispatched by
 *     Scout's "Show me" buttons and its "Take the platform tour" chip
 *     (which uses the legacy `proclaw:start-tour` alias).
 *
 * Steps anchor to `data-tour` attributes and get a box-shadow spotlight +
 * positioned card; steps may declare a route (":tenantId" substituted) and
 * the player navigates there first. Anchorless steps render as a centered
 * card. Missing anchors skip gracefully.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { getUiGuide, type UiGuideStep } from "@proclaw/shared";
import { useAuth } from "@/context/auth-context";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";

const storageKey = (userId: string) => `proclaw.tour.v1.${userId}`;

// Step card widths. Raised from 330 / 360 on 2026-10-01: the cards read small.
const CARD_W = 380;
const CENTERED_CARD_W = 400;

/** Wait for an element to exist (anchors render after route changes). */
function waitForElement(selector: string, timeoutMs = 2500): Promise<Element | null> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const el = document.querySelector(selector);
      if (el) {
        resolve(el);
        return;
      }
      if (Date.now() - started > timeoutMs) {
        resolve(null);
        return;
      }
      setTimeout(tick, 100);
    };
    tick();
  });
}

interface SpotRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function PlatformTour() {
  const { user, isAuthenticated } = useAuth();
  const { tenantId, isLoading: tenantLoading, onboardingComplete, isTenantAdmin } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const navigate = useNavigate();
  const location = useLocation();

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [steps, setSteps] = useState<UiGuideStep[] | null>(null);
  const [stepIndex, setStepIndex] = useState<number | null>(null);
  const [rect, setRect] = useState<SpotRect | null>(null);
  const offeredRef = useRef(false);
  // The active guide's steps, readable from stale closures (event handlers).
  const stepsRef = useRef<UiGuideStep[] | null>(null);
  // The step card, so the interactive click-listener can ignore clicks on it.
  const cardRef = useRef<HTMLDivElement>(null);
  // When each step was entered, and when each `advanceOnEvent` event last
  // fired. Together they let a step count an event that arrived just before it
  // started listening (`catchUpEvent`).
  const enteredAtRef = useRef<number[]>([]);
  const firedAtRef = useRef(new Map<string, number>());

  const markDone = useCallback(() => {
    if (user?.id) {
      try {
        localStorage.setItem(storageKey(user.id), new Date().toISOString());
      } catch {
        /* storage unavailable — the tour just re-offers next visit */
      }
    }
  }, [user?.id]);

  const endGuide = useCallback(
    (remember: boolean) => {
      setSteps(null);
      stepsRef.current = null;
      setStepIndex(null);
      setRect(null);
      if (remember) markDone();
    },
    [markDone],
  );

  const positionStep = useCallback(
    async (
      guideSteps: UiGuideStep[],
      index: number,
      timeoutMs = 8000,
    ): Promise<boolean> => {
      const step = guideSteps[index];
      if (!step) return false;
      // A follow-up step for something the user may have skipped (a channel
      // they never created): nothing to show unless it happened.
      if (step.onlyAfterEvent && !firedAtRef.current.has(step.onlyAfterEvent)) return false;
      if (step.route) {
        let target = step.route.replace(":tenantId", tenantId);
        // Stay on the agent the user has selected. A step route never names an
        // agent, and landing without ?agentId= lets the workspace fall back to
        // another one (last used, or the first in the list).
        const currentAgentId = new URLSearchParams(window.location.search).get("agentId");
        if (currentAgentId && !/[?&]agentId=/.test(target)) {
          target += `${target.includes("?") ? "&" : "?"}agentId=${encodeURIComponent(currentAgentId)}`;
        }
        // Match path AND any query param the step asks for: two workspace modes
        // share a pathname (?mode=chat vs ?mode=workflows), so a pathname-only
        // check would treat "already on /workspace" as done and never switch.
        const [targetPath, targetQuery] = target.split("?");
        const pathMatches = window.location.pathname === targetPath;
        const currentParams = window.location.search.replace(/^\?/, "").split("&");
        const queryMatches =
          !targetQuery || targetQuery.split("&").every((param) => currentParams.includes(param));
        if (!pathMatches || !queryMatches) {
          navigate(target);
        }
      }
      // A step may drive an app action the tour can't click to (open the
      // settings dialog, create + open a demo workflow). Fire it, then let
      // waitForElement below poll until the resulting UI renders.
      if (step.event) {
        window.dispatchEvent(
          new CustomEvent(step.event, { detail: step.eventDetail }),
        );
      }
      if (!step.anchor) {
        setRect(null); // centered card
        return true;
      }
      // Interactive/event steps open a dialog, wizard, modal or canvas after a
      // click, so the anchor mounts late — poll generously (short on Back).
      // Optional steps (controls only some people get) skip after a short look.
      const el = await waitForElement(step.anchor, step.optional ? Math.min(timeoutMs, 800) : timeoutMs);
      if (!el) {
        // Missing anchor: clear the spotlight so the card renders centered
        // (never leave a stale highlight, and don't hang on Back).
        setRect(null);
        return false;
      }
      // Nothing to do on a disabled button (Save with nothing new): skip it.
      if (step.skipIfDisabled && (el as HTMLButtonElement).disabled) return false;
      (el as HTMLElement).scrollIntoView({ block: "nearest" });
      const r = el.getBoundingClientRect();
      const pad = 6;
      setRect({
        top: r.top - pad,
        left: r.left - pad,
        width: r.width + pad * 2,
        height: r.height + pad * 2,
      });
      return true;
    },
    [navigate, tenantId],
  );

  const goToStep = useCallback(
    async (guideSteps: UiGuideStep[], index: number, direction: 1 | -1 = 1) => {
      for (let i = index; i >= 0 && i < guideSteps.length; i += direction) {
        // eslint-disable-next-line no-await-in-loop
        if (await positionStep(guideSteps, i)) {
          enteredAtRef.current[i] = Date.now();
          setStepIndex(i);
          return;
        }
        // Nothing past this step can work without it, so finish here.
        if (guideSteps[i]?.stopIfMissing) break;
      }
      endGuide(true);
    },
    [positionStep, endGuide],
  );

  // Back lands exactly on the previous step, best-effort. Hands-on clicks move
  // the app forward and can't be un-clicked, so a prior anchor may be gone — we
  // reposition with a short wait and fall back to a centered card rather than
  // re-running the forward skip loop (which hung / ended the guide).
  const goBack = useCallback(async () => {
    if (stepIndex === null) return;
    const current = stepsRef.current;
    if (!current) return;
    // Walk backwards to the first earlier step we can actually show. Steps with
    // a route re-navigate (so the view comes back); steps whose control is gone
    // are skipped quickly (short wait) instead of hanging — Back always lands.
    for (let i = stepIndex - 1; i >= 0; i -= 1) {
      // eslint-disable-next-line no-await-in-loop
      if (await positionStep(current, i, 1500)) {
        enteredAtRef.current[i] = Date.now();
        setStepIndex(i);
        return;
      }
    }
  }, [stepIndex, positionStep]);

  const startGuide = useCallback(
    (guideId: string) => {
      const guide = getUiGuide(guideId);
      if (!guide) return;
      setWelcomeOpen(false);
      setSteps(guide.steps);
      stepsRef.current = guide.steps;
      void goToStep(guide.steps, 0);
    },
    [goToStep],
  );

  // First-login offer: once per user (per browser), only after the tenant
  // itself finished company onboarding so the two wizards never stack.
  useEffect(() => {
    if (offeredRef.current || tenantLoading || !isAuthenticated || !user?.id) return;
    if (!onboardingComplete) return;
    if (location.pathname.includes("/onboarding")) return;
    let seen = null;
    try {
      seen = localStorage.getItem(storageKey(user.id));
    } catch {
      seen = "unavailable";
    }
    if (!seen) {
      offeredRef.current = true;
      setWelcomeOpen(true);
    }
  }, [tenantLoading, isAuthenticated, user?.id, onboardingComplete, location.pathname]);

  // On-demand guides (Scout's "Show me" buttons) + legacy tour alias.
  useEffect(() => {
    const onStartGuide = (e: Event) => {
      const guideId = (e as CustomEvent<{ guideId?: string }>).detail?.guideId;
      if (guideId) startGuide(guideId);
    };
    const onStartTour = () => startGuide("platform-basics");
    window.addEventListener("proclaw:start-guide", onStartGuide);
    window.addEventListener("proclaw:start-tour", onStartTour);
    return () => {
      window.removeEventListener("proclaw:start-guide", onStartGuide);
      window.removeEventListener("proclaw:start-tour", onStartTour);
    };
  }, [startGuide]);

  // Keep the spotlight glued to its anchor on resize and scroll (interactive
  // targets can sit inside a scrollable dialog/canvas).
  useEffect(() => {
    if (stepIndex === null) return;
    const reposition = () => {
      if (stepsRef.current) void positionStep(stepsRef.current, stepIndex);
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [stepIndex, positionStep]);

  // Interactive steps advance when the user clicks the highlighted control
  // itself (not a "Next" button). A capture-phase listener fires before the
  // element's own React handler, so we advance even when the click unmounts
  // the target (opens a dialog, navigates). The next step's waitForElement
  // then waits for whatever that click revealed.
  useEffect(() => {
    if (stepIndex === null) return;
    const active = stepsRef.current?.[stepIndex];
    if (!active?.interactive || !active.anchor) return;
    const selector = active.anchor;
    const insideAnchor = (target: Node | null) => {
      if (!target) return false;
      if (cardRef.current?.contains(target)) return false; // ignore the tour card
      const anchorEl = document.querySelector(selector);
      return !!anchorEl && anchorEl.contains(target);
    };
    // A drag step waits for the drop instead (advanceOnEvent), so this only
    // ever listens for clicks.
    const eventName = "click";
    const handler = (e: Event) => {
      if (!insideAnchor(e.target as Node | null)) return;
      window.removeEventListener(eventName, handler, true);
      const current = stepsRef.current;
      if (current && stepIndex !== null) void goToStep(current, stepIndex + 1);
    };
    window.addEventListener(eventName, handler, true);
    return () => window.removeEventListener(eventName, handler, true);
  }, [stepIndex, goToStep]);

  // Record when each event a step of this guide waits on last fired, for
  // the whole guide, so a step can catch one that beat its own listener.
  useEffect(() => {
    if (!steps) return;
    const names = Array.from(
      new Set(
        steps.flatMap((st) => [st.advanceOnEvent, st.onlyAfterEvent]).filter((n): n is string => !!n),
      ),
    );
    const record = (e: Event) => firedAtRef.current.set(e.type, Date.now());
    for (const name of names) window.addEventListener(name, record);
    return () => {
      for (const name of names) window.removeEventListener(name, record);
      firedAtRef.current.clear();
    };
  }, [steps]);

  // Steps that also advance on an app event (the canvas's finished save, a
  // sent chat message, a finished reply). The short wait lets the app render
  // the new state first: the next step may navigate, and the canvas's leave
  // guard still reads "unsaved" until then.
  useEffect(() => {
    if (stepIndex === null) return;
    const step = stepsRef.current?.[stepIndex];
    const eventName = step?.advanceOnEvent;
    if (!step || !eventName) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let advanced = false;
    const advance = () => {
      if (advanced) return;
      advanced = true;
      window.removeEventListener(eventName, advance);
      timer = setTimeout(() => {
        const current = stepsRef.current;
        if (current) void goToStep(current, stepIndex + 1);
      }, 400);
    };
    const since = step.catchUpEvent
      ? enteredAtRef.current[stepIndex - 1] ?? 0
      : enteredAtRef.current[stepIndex] ?? Date.now();
    if ((firedAtRef.current.get(eventName) ?? 0) >= since) advance();
    else window.addEventListener(eventName, advance);
    return () => {
      window.removeEventListener(eventName, advance);
      if (timer) clearTimeout(timer);
    };
  }, [stepIndex, goToStep]);

  // `backIfClosed`: the user closed the dialog (Cancel) without doing the
  // step's action, so go back one step and let them open it again.
  useEffect(() => {
    if (stepIndex === null) return;
    const step = stepsRef.current?.[stepIndex];
    if (!step?.backIfClosed || !step.anchor || !step.advanceOnEvent) return;
    const { anchor, advanceOnEvent } = step;
    const entered = enteredAtRef.current[stepIndex] ?? Date.now();
    const timer = setInterval(() => {
      if (document.querySelector(anchor)) return;
      if ((firedAtRef.current.get(advanceOnEvent) ?? 0) >= entered) return; // done, moving on
      clearInterval(timer);
      void goBack();
    }, 500);
    return () => clearInterval(timer);
  }, [stepIndex, goBack]);

  // Escape always exits, on any step and the welcome modal.
  useEffect(() => {
    if (stepIndex === null && !welcomeOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") endGuide(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stepIndex, welcomeOpen, endGuide]);

  // ── Welcome modal ────────────────────────────────────────────────────
  if (welcomeOpen) {
    // A fresh tenant has no agents yet, so an admin's most useful first move is
    // creating one — offer that instead of the general tour (which needs an
    // agent's chat for several steps). Everyone else gets Platform basics.
    const needsFirstAgent = isTenantAdmin && agents.length === 0;
    const welcomeGuideId = needsFirstAgent ? "create-first-agent" : "platform-basics";
    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
        <div className="mx-4 w-full max-w-sm rounded-2xl border border-border bg-surface p-6 shadow-2xl">
          <div className="mb-3 flex items-center gap-2">
            <Sparkles size={20} className="text-accent" />
            <h2 className="m-0 text-[16px] font-semibold text-text-primary">Welcome to ProClaw</h2>
          </div>
          <p className="m-0 mb-5 text-[13.5px] leading-relaxed text-text-secondary">
            {needsFirstAgent
              ? "This is your AI agent workspace. You don't have any agents yet, so let's create your first one — we'll walk you through it. You can explore the rest anytime from Scout."
              : "This is your AI agent workspace. Take a quick tour: pick an agent, send it a message and see the reply. More short guides, like channels and files, are in Scout whenever you want them."}
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setWelcomeOpen(false);
                markDone();
              }}
              className="rounded-lg px-3 py-2 text-[13px] font-medium text-text-muted hover:bg-surface-hover hover:text-text-primary"
            >
              Skip for now
            </button>
            <button
              onClick={() => startGuide(welcomeGuideId)}
              className="rounded-lg bg-accent px-4 py-2 text-[13px] font-medium text-white hover:bg-accent-hover"
            >
              {needsFirstAgent ? "Create your first agent" : "Take the tour"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Guide steps ──────────────────────────────────────────────────────
  if (stepIndex === null || !steps) return null;
  const step = steps[stepIndex]!;
  const isLast = stepIndex === steps.length - 1;

  // Card position: anchored steps sit beside the spotlight; anchorless
  // steps render centered.
  const centered = !step.anchor || !rect;
  // Interactive = the user clicks the highlighted control to advance (needs a
  // resolved anchor rect). Falls back to informational if the anchor is missing.
  const isInteractive = !!step.interactive && !centered;
  const tooltipBelow = rect ? rect.top + rect.height + 220 < window.innerHeight : true;
  // A tall anchor (e.g. the full-height node palette) can't sit above/below
  // without squashing the card — place it to the side instead so the user can
  // read it next to the highlighted area.
  const tall = !!rect && rect.height > window.innerHeight * 0.5;
  const roomRight = rect ? window.innerWidth - (rect.left + rect.width) : 0;
  // Cap the card to the space actually available and let it scroll, so a tall
  // step near a screen edge can't push its "Next" button off-screen.
  const tooltipStyle: React.CSSProperties = centered
    ? {
        position: "fixed",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: CENTERED_CARD_W,
        maxHeight: "calc(100vh - 24px)",
        overflowY: "auto",
      }
    : tall
      ? {
          position: "fixed",
          top: Math.max(12, Math.min(rect!.top, window.innerHeight - 260)),
          // Beside the area when there is room; when it fills the width (a
          // full-page table), inside its top-right corner, never off-screen.
          ...(roomRight > CARD_W + 30
            ? { left: rect!.left + rect!.width + 12 }
            : rect!.left > CARD_W + 30
              ? { right: window.innerWidth - rect!.left + 12 }
              : { right: Math.max(16, roomRight + 16), top: Math.max(12, rect!.top + 12) }),
          width: CARD_W,
          maxHeight: "calc(100vh - 24px)",
          overflowY: "auto",
        }
      : {
          position: "fixed",
          left: Math.min(Math.max(rect!.left, 12), window.innerWidth - CARD_W - 12),
          ...(tooltipBelow
            ? {
                top: rect!.top + rect!.height + 10,
                maxHeight: window.innerHeight - (rect!.top + rect!.height + 10) - 12,
              }
            : {
                bottom: window.innerHeight - rect!.top + 10,
                maxHeight: rect!.top - 22,
              }),
          width: CARD_W,
          overflowY: "auto",
        };

  return (
    <>
      {/* Click barrier. Interactive steps use a 4-strip mask that leaves the
          highlighted control uncovered so the user can really click it; every
          other step keeps a full-screen shield. `noMask` steps drop the barrier
          entirely (e.g. a modal where the user must type then click). X /
          Escape always exit. */}
      {step.noMask ? null : isInteractive ? (
        <>
          <div
            className="fixed left-0 right-0 top-0 z-[68]"
            style={{ height: Math.max(0, rect!.top) }}
            onClick={() => void 0}
          />
          <div
            className="fixed bottom-0 left-0 right-0 z-[68]"
            style={{ top: rect!.top + rect!.height }}
            onClick={() => void 0}
          />
          <div
            className="fixed left-0 z-[68]"
            style={{ top: rect!.top, width: Math.max(0, rect!.left), height: rect!.height }}
            onClick={() => void 0}
          />
          <div
            className="fixed right-0 z-[68]"
            style={{ top: rect!.top, left: rect!.left + rect!.width, height: rect!.height }}
            onClick={() => void 0}
          />
        </>
      ) : (
        <div
          className={`fixed inset-0 z-[68] ${centered ? "bg-black/45" : ""}`}
          onClick={() => void 0}
        />
      )}
      {/* Spotlight cutout */}
      {!centered && (
        <div
          className="pointer-events-none fixed z-[69] rounded-lg border-2 border-accent transition-all duration-200"
          style={{
            top: rect!.top,
            left: rect!.left,
            width: rect!.width,
            height: rect!.height,
            // Dim everything outside the highlighted area for every anchored
            // step — including noMask popup/form steps. The dim is purely
            // visual (this layer is pointer-events-none), so noMask steps stay
            // fully interactive while their surroundings go dark like the rest.
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
          }}
        />
      )}
      {/* Step card */}
      <div
        ref={cardRef}
        style={tooltipStyle}
        className="z-[70] rounded-xl border border-border bg-surface p-5 shadow-2xl"
      >
        <div className="mb-1.5 flex items-start justify-between gap-2">
          <p className="m-0 text-[15.5px] font-semibold text-text-primary">{step.title}</p>
          <button
            onClick={() => endGuide(true)}
            aria-label="End tour"
            className="rounded p-0.5 text-text-muted hover:text-text-primary"
          >
            <X size={16} />
          </button>
        </div>
        <p className="m-0 mb-4 text-[13.5px] leading-relaxed text-text-secondary">{step.body}</p>
        {step.suggestions && step.suggestions.length > 0 && (
          // Fills the chat box only; the user still sends it themselves.
          <div className="mb-4 flex flex-col gap-1.5">
            {step.suggestions.map((text) => (
              <button
                key={text}
                type="button"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("proclaw:composer-fill", { detail: { text } }))
                }
                className="rounded-lg border border-border px-3 py-2 text-left text-[13px] text-text-primary transition-colors hover:border-accent/50 hover:bg-surface-hover"
              >
                {text}
              </button>
            ))}
          </div>
        )}
        {/* What the user has to do on a hands-on step, on its own line so it
            never squeezes beside the step dots. */}
        {(isInteractive || (step.requireEvent && step.advanceOnEvent)) && (
          <p className="m-0 mb-3 text-[12.5px] italic text-text-muted">
            {isInteractive ? "Click the highlighted control" : step.waitHint ?? "Waiting…"}
          </p>
        )}
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-[7px] w-[7px] rounded-full ${i === stepIndex ? "bg-accent" : "bg-border"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {stepIndex > 0 && (
              <button
                onClick={goBack}
                className="rounded-lg px-3 py-1.5 text-[13.5px] font-medium text-text-muted hover:bg-surface-hover hover:text-text-primary"
              >
                Back
              </button>
            )}
            {isInteractive || (step.requireEvent && step.advanceOnEvent) ? null : (
              <button
                onClick={() => (isLast ? endGuide(true) : void goToStep(steps, stepIndex + 1))}
                className="rounded-lg bg-accent px-4 py-2 text-[13.5px] font-medium text-white hover:bg-accent-hover"
              >
                {isLast ? "Done" : "Next"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
